import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  ArrowLeft, AlertTriangle, Sparkles, Heart, Zap, BookOpen, 
  Check, Grid3x3, User, TrendingUp, Moon as MoonIcon, Menu, Crown, MapPin, FileText, Pen
} from 'lucide-react';
import EnhancedLoshuGrid from '../components/EnhancedLoshuGrid';

const MENU_ITEMS = [
  { id: 'grid', label: 'Lo Shu Grid', icon: Grid3x3 },
  { id: 'numbers', label: 'Core Numbers', icon: User },
  { id: 'dc-combo', label: 'D-C Combination', icon: Heart },
  { id: 'full-report', label: 'Full Report', icon: Sparkles },
  { id: 'corrected-name', label: 'Corrected Name', icon: Crown },
  { id: 'yogas', label: 'Yogas & Strengths', icon: Zap },
  { id: 'deficits', label: 'Missing Energies', icon: AlertTriangle },
  { id: 'integration', label: 'Integration Path', icon: BookOpen }
];

function Results() {
  const [results, setResults] = useState(null);
  const [activePanel, setActivePanel] = useState('grid');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [correctedName, setCorrectedName] = useState(null);
  const pollRef = React.useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const resultsStr = localStorage.getItem('analysisResults');
    if (resultsStr) {
      setResults(JSON.parse(resultsStr));
    } else {
      navigate('/dashboard');
    }
    // Fetch corrected name
    axios.get('/api/numerology/corrected-name').then(r => {
      if (r.data.corrected_name) setCorrectedName(r.data);
    }).catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [navigate]);

  if (!results) {
    return (
      <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loader" />
      </div>
    );
  }

  const { birth_numbers, dc_combination, loshu_grid, yogas, analysis, missing_deficits, professional_insight, integration_summary } = results;

  const HOUSE_INFO = {
    4: { label: 'Education & Knowledge', color: '#A78BFA' },
    9: { label: 'Fame & Recognition', color: '#F59E0B' },
    2: { label: 'Relationships & Emotions', color: '#EC4899' },
    3: { label: 'Expression & Creativity', color: '#7C3AED' },
    5: { label: 'Balance & Communication', color: '#00D9FF' },
    7: { label: 'Spirituality & Research', color: '#10B981' },
    8: { label: 'Wealth & Authority', color: '#FB923C' },
    1: { label: 'Self & Career', color: '#EF4444' },
    6: { label: 'Love & Home', color: '#F472B6' }
  };

  const renderPanelContent = () => {
    switch (activePanel) {
      case 'grid':
        return (
          <div>
            {/* DOB Header */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '0.8125rem', color: '#6B7280' }}>Date of Birth</p>
              <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#00D9FF', letterSpacing: '2px' }}>
                {birth_numbers.birth_date || 'N/A'}
              </p>
            </div>

            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Lo Shu Grid</h2>
            <EnhancedLoshuGrid gridData={loshu_grid} birthNumbers={birth_numbers} />
            
            {/* House Meanings */}
            <div style={{ marginTop: '1.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#9CA3AF' }}>What Each House Represents</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
                {[4,9,2,3,5,7,8,1,6].map(n => {
                  const info = HOUSE_INFO[n];
                  const count = parseInt(loshu_grid?.counts?.[String(n)] || 0);
                  const isMissing = count === 0;
                  return (
                    <div key={n} style={{
                      padding: '0.625rem', borderRadius: '8px', textAlign: 'center',
                      background: isMissing ? '#1E213950' : `${info.color}10`,
                      border: `1px solid ${isMissing ? '#374151' : info.color + '40'}`,
                      opacity: isMissing ? 0.6 : 1
                    }}>
                      <span style={{ fontSize: '1.125rem', fontWeight: 700, color: info.color }}>{n}</span>
                      <p style={{ fontSize: '0.6875rem', color: isMissing ? '#6B7280' : '#D1D5DB', marginTop: '0.125rem', lineHeight: 1.3 }}>
                        {info.label}
                      </p>
                      {count > 1 && <span style={{ fontSize: '0.625rem', color: '#F59E0B' }}>x{count}</span>}
                      {isMissing && <span style={{ fontSize: '0.625rem', color: '#EF4444' }}>Missing</span>}
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Plane Analysis */}
            {yogas?.plane_analysis && (
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem' }}>Plane Strengths</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                  {Object.entries(yogas.plane_analysis).map(([plane, data]) => (
                    <div key={plane} style={{ 
                      padding: '1rem', background: '#1E2139', borderRadius: '10px',
                      border: `2px solid ${data.strength === 'strong' ? '#10B981' : data.strength === 'moderate' ? '#F59E0B' : '#6B7280'}`,
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '1.75rem', fontWeight: 700, 
                        color: data.strength === 'strong' ? '#10B981' : data.strength === 'moderate' ? '#F59E0B' : '#9CA3AF' }}>
                        {data.count}
                      </div>
                      <p style={{ fontSize: '0.875rem', textTransform: 'capitalize', fontWeight: 600 }}>{plane}</p>
                      <p style={{ fontSize: '0.75rem', color: '#6B7280', lineHeight: 1.3, marginTop: '0.25rem' }}>{data.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'numbers':
        // Build repetition data
        const reps = [];
        if (loshu_grid?.counts) {
          Object.entries(loshu_grid.counts).forEach(([num, count]) => {
            const c = parseInt(count);
            if (c >= 3) reps.push({ num, count: c, type: 'TRIPLE', color: '#EF4444' });
            else if (c === 2) reps.push({ num, count: c, type: 'DOUBLE', color: '#F59E0B' });
          });
        }
        
        const DRIVER_DESC = {
          1: 'Leadership, independence, initiative. You think "I decide" first. Strong self-identity.',
          2: 'Intuition, diplomacy, emotional intelligence. You sense before you think. Moon-driven decisions.',
          3: 'Expression, optimism, expansion. You lead with ideas and teaching. Jupiter-guided vision.',
          4: 'Structure, rebellion, unconventional thinking. Rahu energy creates sudden changes.',
          5: 'Communication, adaptability, speed. Mercury mind processes fast. Business intelligence.',
          6: 'Nurturing, aesthetics, relationships. Venus makes you seek beauty, love, comfort.',
          7: 'Research, spirituality, detachment. Ketu gives deep analysis but isolation tendency.',
          8: 'Authority, power, karma. Saturn demands discipline. Delayed but lasting success.',
          9: 'Courage, energy, competition. Mars fire drives action. Strong but impulsive.'
        };
        const CONDUCTOR_DESC = {
          1: 'Life pushes you toward self-reliance and leadership roles, whether you want it or not.',
          2: 'Life brings partnerships, emotional lessons, and the need to master diplomacy.',
          3: 'Life expands through teaching, creativity, and being seen as a mentor/guide.',
          4: 'Life tests you with sudden changes. Structure vs chaos is your recurring theme.',
          5: 'Life rewards communication skills. Money flows through networking and adaptability.',
          6: 'Life centers around family, relationships, and creating beauty/harmony.',
          7: 'Life demands deep research and spiritual growth. Success comes through specialization.',
          8: 'Life is karmic. Hard work, delays, but massive rewards for persistence.',
          9: 'Life is action-oriented. You are pushed into competitive situations and leadership.'
        };
        const KUA_DESC = {
          1: 'North energy. Career focus. Water element favors flexibility.',
          2: 'Southwest energy. Relationship focus. Earth element favors stability.',
          3: 'East energy. Health & family. Wood element favors growth.',
          4: 'Southeast energy. Wealth direction. Wood element favors prosperity.',
          5: 'Center energy (male=2, female=8). Grounding force.',
          6: 'Northwest energy. Mentor & travel. Metal element favors authority.',
          7: 'West energy. Children & creativity. Metal element favors precision.',
          8: 'Northeast energy. Knowledge & self-mastery. Earth element.',
          9: 'South energy. Fame & recognition. Fire element favors visibility.'
        };

        return (
          <div>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Your Core Numbers & Planets</h2>
            
            <div style={{ display: 'grid', gap: '1rem' }}>
              {/* Driver */}
              <div style={{ padding: '1.25rem', background: '#00D9FF10', border: '2px solid #00D9FF40', borderRadius: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#00D9FF20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.75rem', fontWeight: 800, color: '#00D9FF' }}>
                    {birth_numbers.driver}
                  </div>
                  <div>
                    <p style={{ fontWeight: 700, fontSize: '1rem' }}>Driver (Psychic Number)</p>
                    <p style={{ fontSize: '0.875rem', color: '#00D9FF' }}>{birth_numbers.driver_planet?.planet || 'N/A'}</p>
                  </div>
                </div>
                <p style={{ fontSize: '0.8125rem', color: '#9CA3AF', marginBottom: '0.5rem' }}>How your MIND works. Your inner personality, first reaction, instinct.</p>
                <p style={{ fontSize: '0.875rem', color: '#D1D5DB', lineHeight: 1.6 }}>{DRIVER_DESC[birth_numbers.driver] || ''}</p>
              </div>

              {/* Conductor */}
              <div style={{ padding: '1.25rem', background: '#7C3AED10', border: '2px solid #7C3AED40', borderRadius: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#7C3AED20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.75rem', fontWeight: 800, color: '#7C3AED' }}>
                    {birth_numbers.conductor}
                  </div>
                  <div>
                    <p style={{ fontWeight: 700, fontSize: '1rem' }}>Conductor (Destiny Number)</p>
                    <p style={{ fontSize: '0.875rem', color: '#7C3AED' }}>{birth_numbers.conductor_planet?.planet || 'N/A'}</p>
                  </div>
                </div>
                <p style={{ fontSize: '0.8125rem', color: '#9CA3AF', marginBottom: '0.5rem' }}>What LIFE pushes you toward. Your destiny, external perception, life lessons.</p>
                <p style={{ fontSize: '0.875rem', color: '#D1D5DB', lineHeight: 1.6 }}>{CONDUCTOR_DESC[birth_numbers.conductor] || ''}</p>
              </div>

              {/* KUA */}
              {birth_numbers.kua_number && (
                <div style={{ padding: '1.25rem', background: '#10B98110', border: '2px solid #10B98140', borderRadius: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#10B98120', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.75rem', fontWeight: 800, color: '#10B981' }}>
                      {birth_numbers.kua_number}
                    </div>
                    <div>
                      <p style={{ fontWeight: 700, fontSize: '1rem' }}>KUA Number</p>
                      <p style={{ fontSize: '0.875rem', color: '#10B981' }}>{birth_numbers.kua_info?.planet || ''}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: '0.8125rem', color: '#9CA3AF', marginBottom: '0.5rem' }}>Your ENERGY direction. Best directions, Vastu alignment, feng shui compatibility.</p>
                  <p style={{ fontSize: '0.875rem', color: '#D1D5DB', lineHeight: 1.6 }}>{KUA_DESC[birth_numbers.kua_number] || ''}</p>
                </div>
              )}
            </div>

            {/* Repetition Patterns */}
            {reps.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#F59E0B' }}>Repetition Patterns in Your Grid</h3>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {reps.map((r, i) => (
                    <div key={i} style={{
                      padding: '0.75rem 1rem', borderRadius: '8px',
                      background: `${r.color}10`, borderLeft: `4px solid ${r.color}`,
                      display: 'flex', alignItems: 'center', gap: '0.75rem'
                    }}>
                      <span style={{ fontWeight: 800, fontSize: '1.25rem', color: r.color, minWidth: '30px' }}>
                        {r.num}x{r.count}
                      </span>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: '0.8125rem', color: r.color }}>{r.type} REPETITION</span>
                        <p style={{ fontSize: '0.8125rem', color: '#D1D5DB', marginTop: '0.125rem' }}>
                          {r.count >= 3 
                            ? `Number ${r.num} appears ${r.count} times — INTENSE amplification. This energy dominates your grid and creates powerful but potentially distorting patterns.`
                            : `Number ${r.num} appears twice — Amplified energy. Stronger presence of this vibration in your life.`
                          }
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Missing Numbers Quick View */}
            {loshu_grid?.missing?.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <h3 style={{ fontSize: '1.125rem', marginBottom: '0.75rem', color: '#EF4444' }}>Missing Numbers</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {loshu_grid.missing.map(n => (
                    <span key={n} style={{
                      padding: '0.375rem 0.875rem', background: '#EF444415', border: '1px solid #EF444440',
                      borderRadius: '8px', fontSize: '0.875rem', color: '#FCA5A5', fontWeight: 600
                    }}>
                      {n} — {HOUSE_INFO[n]?.label || 'Gap'}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'dc-combo':
        return dc_combination ? (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>{dc_combination.name}</h2>
            <div style={{ 
              padding: '2rem', 
              background: 'rgba(245, 158, 11, 0.1)', 
              borderRadius: '12px', 
              marginBottom: '2rem',
              borderLeft: '4px solid #F59E0B'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', color: '#F59E0B' }}>Life Theme</h3>
              <p style={{ fontSize: '1.125rem', lineHeight: 1.7 }}>{dc_combination.theme}</p>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
              <div style={{ padding: '2rem', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '12px', borderLeft: '4px solid #10B981' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1.25rem', color: '#10B981', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Check size={22} /> Strengths
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {dc_combination.strengths?.map((strength, i) => (
                    <li key={i} style={{ 
                      marginBottom: '1rem', 
                      paddingLeft: '1.5rem', 
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: 1.6
                    }}>
                      <span style={{ position: 'absolute', left: 0, color: '#10B981', fontSize: '1.25rem' }}>✓</span>
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div style={{ padding: '2rem', background: 'rgba(251, 146, 60, 0.1)', borderRadius: '12px', borderLeft: '4px solid #FB923C' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1.25rem', color: '#FB923C', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <AlertTriangle size={22} /> Challenges
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {dc_combination.challenges?.map((challenge, i) => (
                    <li key={i} style={{ 
                      marginBottom: '1rem', 
                      paddingLeft: '1.5rem', 
                      position: 'relative',
                      fontSize: '1rem',
                      lineHeight: 1.6
                    }}>
                      <span style={{ position: 'absolute', left: 0, color: '#FB923C', fontSize: '1.25rem' }}>!</span>
                      {challenge}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div style={{ 
              padding: '2rem', 
              background: 'rgba(59, 130, 246, 0.1)', 
              borderRadius: '12px',
              borderLeft: '4px solid #3B82F6'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', color: '#3B82F6' }}>Integration Path</h3>
              <p style={{ fontSize: '1.0625rem', lineHeight: 1.8, fontStyle: 'italic' }}>
                {dc_combination.life_pattern}
              </p>
            </div>
          </div>
        ) : null;

      case 'full-report':
        // Show loading state if report is still generating
        if (!professional_insight || professional_insight.includes('Generating')) {
          return (
            <div>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Your Complete Numerology Report</h2>
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <div style={{ width: '48px', height: '48px', border: '3px solid #7C3AED30', borderTopColor: '#7C3AED', borderRadius: '50%', margin: '0 auto 1.5rem', animation: 'spin 1s linear infinite' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <p style={{ color: '#D1D5DB', fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Generating Your Premium Report...
                </p>
                <p style={{ color: '#6B7280', fontSize: '0.875rem', marginBottom: '2rem' }}>
                  Takes about 1-2 minutes. Click the button below when ready.
                </p>
                <button
                  data-testid="load-report-btn"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch('/api/history?limit=1', { headers: { 'Authorization': 'Bearer ' + token } });
                      const data = await res.json();
                      const newPI = data?.readings?.[0]?.output?.professional_insight || '';
                      if (newPI.length > 200 && !newPI.includes('Generating')) {
                        const updated = { ...results, professional_insight: newPI };
                        setResults(updated);
                        localStorage.setItem('analysisResults', JSON.stringify(updated));
                      } else {
                        // Change button text temporarily instead of alert
                        const btn = document.querySelector('[data-testid="load-report-btn"]');
                        if (btn) { btn.textContent = 'Still generating... try again in 1 min'; btn.style.background = '#374151'; }
                        setTimeout(() => { if (btn) { btn.textContent = 'Load Report'; btn.style.background = '#7C3AED'; } }, 5000);
                      }
                    } catch (e) {
                      const btn = document.querySelector('[data-testid="load-report-btn"]');
                      if (btn) { btn.textContent = 'Retry in a moment...'; setTimeout(() => { if (btn) btn.textContent = 'Load Report'; }, 3000); }
                    }
                  }}
                  style={{ padding: '0.75rem 2rem', background: '#7C3AED', border: 'none', borderRadius: '10px', color: '#FFF', fontWeight: 600, cursor: 'pointer', fontSize: '1rem' }}
                >
                  Load Report
                </button>
                <p style={{ color: '#374151', fontSize: '0.75rem', marginTop: '1rem' }}>
                  Click after 1-2 minutes to load the completed premium report
                </p>
              </div>
            </div>
          );
        }
        return professional_insight ? (
          <div>
            {/* Hero header */}
            <div style={{ textAlign: 'center', marginBottom: '2rem', padding: '1.5rem', background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)', borderRadius: '16px', border: '1px solid #334155' }}>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '0.25rem' }}>Your Complete Numerology Report</h2>
              <p style={{ color: '#94A3B8', fontSize: '0.875rem' }}>
                Personalized for {birth_numbers.full_name || 'You'} | DOB: {birth_numbers.birth_date || ''} | Driver {birth_numbers.driver} | Conductor {birth_numbers.conductor} | KUA {birth_numbers.kua_number}
              </p>
            </div>
            
            {/* Render as premium cards */}
            {(() => {
              const sections = [];
              let currentSection = null;
              const COLORS = ['#F59E0B', '#EC4899', '#10B981', '#3B82F6', '#A78BFA', '#FB923C', '#EF4444', '#14B8A6', '#8B5CF6', '#06B6D4'];
              let cIdx = 0;
              
              const clean = (t) => t.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1').replace(/^#{1,6}\s*/, '')
                .replace(/[\–\-–—]\s*[Pp]age\s*\d+[\-\d]*/g, '')
                .replace(/\([Pp]age\s*\d+[\-\d]*\)/g, '')
                .replace(/[Pp]age\s*\d+[\-\d]*[;,.]?/g, '')
                .replace(/from the book set[:\s]*/gi, '')
                .replace(/according to the (?:book|text|source)[,\s]*/gi, '')
                .replace(/\s{2,}/g, ' ').trim();
              
              professional_insight.split('\n').forEach((line) => {
                const t = line.trim();
                if (!t) return;
                
                // Section header detection (any number of # marks)
                if (/^#{1,6}\s/.test(t) || /^\d+[.)]\s+[A-Z🔮🧠🧩💰👔❤⚡🔁🚀🔥]/.test(t)) {
                  if (currentSection) sections.push(currentSection);
                  const title = clean(t).replace(/^#+\s*/, '').replace(/^━+$/, '');
                  if (title) {
                    currentSection = { title, color: COLORS[cIdx % COLORS.length], items: [], isSubHeader: /^#{3,}/.test(t) };
                    cIdx++;
                  }
                  return;
                }
                
                if (/^[━─═-]{3,}$/.test(t)) return;
                if (!currentSection) { currentSection = { title: '', color: COLORS[0], items: [] }; }
                currentSection.items.push(clean(t));
              });
              if (currentSection) sections.push(currentSection);
              
              return sections.map((sec, sIdx) => {
                if (!sec.title && sec.items.length === 0) return null;
                
                return (
                  <div key={sIdx} style={{
                    marginBottom: '1rem',
                    background: '#0F172A',
                    borderRadius: '12px',
                    border: `1px solid ${sec.color}25`,
                    overflow: 'hidden'
                  }}>
                    {/* Section header bar */}
                    {sec.title && (
                      <div style={{
                        padding: sec.isSubHeader ? '0.75rem 1.25rem' : '1rem 1.25rem',
                        background: `${sec.color}12`,
                        borderBottom: `2px solid ${sec.color}30`,
                        display: 'flex', alignItems: 'center', gap: '0.75rem'
                      }}>
                        <div style={{
                          width: sec.isSubHeader ? '6px' : '8px',
                          height: sec.isSubHeader ? '6px' : '8px',
                          borderRadius: '50%',
                          background: sec.color,
                          boxShadow: `0 0 8px ${sec.color}60`,
                          flexShrink: 0
                        }} />
                        <h3 style={{
                          fontSize: sec.isSubHeader ? '0.9375rem' : '1.125rem',
                          fontWeight: 700,
                          color: sec.isSubHeader ? '#E2E8F0' : sec.color,
                          margin: 0
                        }}>
                          {sec.title}
                        </h3>
                      </div>
                    )}
                    
                    {/* Content */}
                    <div style={{ padding: '1rem 1.25rem' }}>
                      {sec.items.map((item, iIdx) => {
                        const cleaned = clean(item);
                        
                        // Bullet point
                        if (/^[-•]\s/.test(item)) {
                          return (
                            <div key={iIdx} style={{
                              display: 'flex', gap: '0.625rem', marginBottom: '0.625rem',
                              padding: '0.5rem 0.75rem',
                              background: '#1E293B',
                              borderRadius: '8px',
                              borderLeft: `3px solid ${sec.color}40`
                            }}>
                              <span style={{ color: sec.color, flexShrink: 0, marginTop: '1px' }}>&#x2022;</span>
                              <span style={{ fontSize: '0.9375rem', lineHeight: 1.7, color: '#CBD5E1' }}>
                                {clean(item.replace(/^[-•]\s/, ''))}
                              </span>
                            </div>
                          );
                        }
                        
                        // Sub-label (bold standalone line)
                        if (/^\*\*/.test(item) || (cleaned.endsWith(':') && cleaned.length < 80)) {
                          return (
                            <p key={iIdx} style={{
                              fontSize: '0.9375rem', fontWeight: 700, color: '#F1F5F9',
                              marginTop: '0.875rem', marginBottom: '0.375rem'
                            }}>
                              {cleaned}
                            </p>
                          );
                        }
                        
                        // Regular paragraph
                        return (
                          <p key={iIdx} style={{
                            fontSize: '0.9375rem', lineHeight: 1.8, color: '#94A3B8',
                            marginBottom: '0.5rem'
                          }}>
                            {cleaned}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        ) : null;

      case 'corrected-name':
        return (
          <div>
            {correctedName ? (
              <div>
                {/* Grand Name Display */}
                <div style={{ textAlign: 'center', padding: '2.5rem', marginBottom: '2rem', background: 'linear-gradient(135deg, rgba(201,169,97,0.12), rgba(10,15,42,0.95))', borderRadius: '20px', border: `2px solid #C9A961`, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg, transparent, #C9A961, transparent)` }} />
                  <Crown size={40} color={'#C9A961'} style={{ marginBottom: '0.75rem' }} />
                  <p style={{ color: '#C9A961', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.15em', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Your Corrected Name</p>
                  <h1 data-testid="corrected-name-display" style={{ fontSize: '2.5rem', fontWeight: 800, color: '#FFF', marginBottom: '0.5rem', fontFamily: 'Space Grotesk, serif' }}>{correctedName.corrected_name}</h1>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '0.75rem' }}>
                    <span style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981', padding: '0.4rem 1rem', borderRadius: '8px', fontSize: '0.88rem', fontWeight: 700 }}>
                      Compound {correctedName.compound_number} — {correctedName.compound_info?.name}
                    </span>
                    <span style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6', padding: '0.4rem 1rem', borderRadius: '8px', fontSize: '0.88rem' }}>
                      = {correctedName.single_number}
                    </span>
                  </div>
                </div>

                {/* Why This Name Is Powerful */}
                <div style={{ marginBottom: '1.5rem', padding: '1.25rem', borderRadius: '14px', background: '#0F172A', border: '1px solid rgba(16,185,129,0.2)' }}>
                  <h3 style={{ color: '#10B981', fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Why This Name Is Powerful</h3>
                  <p style={{ color: '#D1D5DB', fontSize: '0.95rem', lineHeight: 1.8, marginBottom: '0.75rem' }}>{correctedName.compound_info?.effect}</p>
                  {correctedName.reasons?.map((r, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                      <Check size={16} color="#10B981" />
                      <span style={{ color: '#E5E7EB', fontSize: '0.9rem' }}>{r}</span>
                    </div>
                  ))}
                  <p style={{ color: '#6B7280', fontSize: '0.82rem', marginTop: '0.75rem' }}>
                    Previous: {correctedName.original_name} | Goal: {correctedName.goal}
                  </p>
                </div>

                {/* Activation Instructions — 30 Actionable Items */}
                <div style={{ padding: '1.25rem', borderRadius: '14px', background: '#0F172A', border: '1px solid #C9A96140' }}>
                  <h3 style={{ color: '#C9A961', fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FileText size={18} /> How to Activate Your New Name
                  </h3>
                  <p style={{ color: '#D1D5DB', fontSize: '0.9rem', lineHeight: 1.8, marginBottom: '1.25rem' }}>
                    A name works when it is <strong style={{ color: '#FFF' }}>used consistently everywhere</strong>. The more places you activate it, the faster the vibration builds. Follow this checklist.
                  </p>
                  {[
                    { title: 'Basic Activation (Start Immediately)', color: '#10B981', items: [
                      ['Write your new name daily — 54 or 108 times on paper', 'Day 1'],
                      ['Speak your name aloud 11-21 times every morning', 'Daily'],
                      ['Introduce yourself with the new name in conversations', 'Daily'],
                      ['Visualize your name in your mind during quiet moments', 'Daily'],
                    ]},
                    { title: 'Identity & Signature', color: '#3B82F6', items: [
                      ['Fix ONE signature style — practice and use only that', 'Week 1'],
                      ['Use the exact same spelling everywhere — no variations', 'Important'],
                      ['Do NOT mix old and new name — commit fully', 'Critical'],
                      ['If using parental initial, include it consistently', 'If PII'],
                    ]},
                    { title: 'Digital Activation', color: '#8B5CF6', items: [
                      ['Update your WhatsApp display name', 'Week 1'],
                      ['Update your email signature and display name', 'Week 1'],
                      ['Update Instagram, LinkedIn, Facebook, Twitter profiles', 'Week 1'],
                      ['Update your website, portfolio, or business page', 'Week 2'],
                      ['Update caller ID and phone contact name', 'Week 2'],
                    ]},
                    { title: 'Social Activation', color: '#EC4899', items: [
                      ['Tell close friends and family about the name change', 'Week 1'],
                      ['Ask people to start using your new name', 'Ongoing'],
                      ['Use the new name in all daily conversations', 'Daily'],
                      ['Gently correct people when they use the old spelling', 'Ongoing'],
                    ]},
                    { title: 'Money & Business Activation', color: '#F59E0B', items: [
                      ['Use new name in all invoices and receipts', 'Powerful'],
                      ['Use in business deals, proposals, and presentations', 'Powerful'],
                      ['Update bank account name (connects money energy)', 'Month 1'],
                      ['Use in all contracts and legal agreements', 'Month 1'],
                    ]},
                    { title: 'Fast-Track / Public Activation', color: '#06B6D4', items: [
                      ['Use name in public appearances and events', 'Optional'],
                      ['Use in media interviews or press mentions', 'Optional'],
                      ['Launch a project or brand with the new name', 'Optional'],
                      ['Make a public announcement of the name change', 'Optional'],
                    ]},
                    { title: 'Spiritual Activation', color: '#A855F7', items: [
                      ['Start using the name on an astrologically favorable date', 'Recommended'],
                      ['Set a daily intention/affirmation with your new name', 'Daily'],
                      ['Meditate for 5 min visualizing the name in golden light', 'Daily'],
                    ]},
                    { title: 'Consistency Rules (Most Important!)', color: '#EF4444', items: [
                      ['Use the name EVERYWHERE — no exceptions, no old spelling', 'Critical'],
                      ['Maintain consistently for minimum 21-90 days for full activation', 'Critical'],
                    ]},
                  ].map((section, si) => (
                    <div key={si} style={{ marginBottom: '0.85rem' }}>
                      <p style={{ color: section.color, fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.4rem' }}>{section.title}</p>
                      <div style={{ display: 'grid', gap: '0.25rem' }}>
                        {section.items.map((item, ii) => (
                          <div key={ii} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.65rem', borderRadius: '6px', background: section.color + '08', borderLeft: '3px solid ' + section.color + '40' }}>
                            <Check size={13} color={section.color} style={{ flexShrink: 0 }} />
                            <span style={{ color: '#D1D5DB', fontSize: '0.84rem', flex: 1 }}>{item[0]}</span>
                            <span style={{ color: section.color, fontSize: '0.63rem', fontWeight: 700, padding: '0.1rem 0.35rem', borderRadius: '4px', background: section.color + '15', whiteSpace: 'nowrap' }}>{item[1]}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <Crown size={48} color="#334155" style={{ marginBottom: '1rem' }} />
                <h2 style={{ fontSize: '1.5rem', color: '#FFF', marginBottom: '0.5rem' }}>No Corrected Name Selected Yet</h2>
                <p style={{ color: '#9CA3AF', marginBottom: '1.5rem' }}>Use the Name Correction Engine to find your ideal name based on compound number science.</p>
                <button onClick={() => navigate('/name-correction')} style={{ background: '#C9A961', color: '#0A0F2A', padding: '0.75rem 2rem', borderRadius: '10px', fontWeight: 700, border: 'none', cursor: 'pointer', fontSize: '1rem' }}>
                  Go to Name Correction
                </button>
              </div>
            )}
          </div>
        );

      case 'yogas':
        return yogas?.arrows_of_strength?.length > 0 ? (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Yogas (Arrows of Strength)</h2>
            {yogas.arrows_of_strength.map((yoga, i) => (
              <div key={i} style={{ 
                padding: '2rem', 
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)', 
                borderRadius: '16px', 
                borderLeft: '4px solid #10B981',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10B981' }}>{yoga.name}</h3>
                  <span style={{ 
                    padding: '0.5rem 1rem',
                    background: 'rgba(16, 185, 129, 0.2)', 
                    color: '#10B981', 
                    borderRadius: '8px',
                    textTransform: 'capitalize',
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }}>
                    {yoga.intensity}
                  </span>
                </div>
                <p style={{ fontSize: '0.9375rem', color: '#9CA3AF', marginBottom: '1rem' }}>
                  Planets: {yoga.planets?.join(', ')}
                </p>
                <p style={{ fontSize: '1.125rem', lineHeight: 1.8, color: '#E5E7EB' }}>
                  <strong style={{ color: '#10B981' }}>Benefit:</strong> {yoga.benefit}
                </p>
              </div>
            ))}

            {/* Enhanced Yogas from derived numbers */}
            {loshu_grid?.enhanced_yogas?.length > 0 && (
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#F59E0B' }}>
                  ⭐ Yogas Activated by Derived Numbers
                </h3>
                {loshu_grid.enhanced_yogas.map((yoga, i) => (
                  <div key={i} style={{ 
                    padding: '2rem', 
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.05) 100%)',
                    borderRadius: '16px',
                    border: '3px solid #F59E0B',
                    marginBottom: '1.5rem'
                  }}>
                    <h4 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.75rem', color: '#F59E0B' }}>
                      {yoga.name}
                    </h4>
                    <p style={{ fontSize: '0.9375rem', color: '#9CA3AF', marginBottom: '1rem' }}>
                      Completed by: {yoga.completed_by.join(', ')}
                    </p>
                    <p style={{ fontSize: '1.125rem', lineHeight: 1.8, color: '#E5E7EB' }}>
                      <strong style={{ color: '#F59E0B' }}>Special Benefit:</strong> {yoga.benefit}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : <div><h2>No yogas detected</h2></div>;

      case 'deficits':
        return missing_deficits?.length > 0 ? (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Missing Planetary Energies</h2>
            {missing_deficits.map((deficit, i) => (
              <div key={i} style={{ 
                padding: '2rem', 
                background: 'linear-gradient(135deg, #1A1B2E 0%, #151827 100%)', 
                borderRadius: '16px', 
                border: '2px solid rgba(251, 146, 60, 0.3)',
                marginBottom: '2rem'
              }}>
                <div style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #2D3748' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #FB923C 0%, #EA580C 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2rem',
                      fontWeight: 700,
                      color: '#FFF'
                    }}>
                      {deficit.number}
                    </div>
                    <div>
                      <h3 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.25rem' }}>{deficit.planet}</h3>
                      <p style={{ fontSize: '1rem', color: '#9CA3AF' }}>{deficit.domain}</p>
                    </div>
                  </div>
                </div>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#FB923C' }}>Life Challenges</h4>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {deficit.life_challenges?.map((challenge, j) => (
                      <div key={j} style={{ 
                        padding: '1rem', 
                        background: 'rgba(251, 146, 60, 0.08)', 
                        borderRadius: '8px',
                        fontSize: '1rem',
                        lineHeight: 1.6,
                        borderLeft: '3px solid #FB923C'
                      }}>
                        {challenge}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#F59E0B' }}>
                    Observed Patterns (Relatives)
                  </h4>
                  <div style={{ padding: '1rem', background: 'rgba(245, 158, 11, 0.08)', borderRadius: '8px', borderLeft: '3px solid #F59E0B' }}>
                    <p style={{ fontSize: '1rem', color: '#D1D5DB', marginBottom: '0.75rem', fontWeight: 500 }}>
                      Affects: {deficit.relative}
                    </p>
                    {deficit.observed_patterns?.map((pattern, j) => (
                      <p key={j} style={{ fontSize: '0.9375rem', color: '#9CA3AF', marginBottom: '0.5rem', fontStyle: 'italic', lineHeight: 1.6 }}>
                        • {pattern}
                      </p>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#10B981' }}>
                    Remedies to Strengthen {deficit.planet}
                  </h4>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {deficit.remedies?.map((remedy, j) => (
                      <div key={j} style={{ 
                        padding: '1.25rem', 
                        background: 'rgba(16, 185, 129, 0.08)', 
                        borderRadius: '10px',
                        borderLeft: '3px solid #10B981'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                          <span style={{ 
                            display: 'inline-block',
                            padding: '0.5rem 0.75rem',
                            background: 'rgba(16, 185, 129, 0.2)',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: '#10B981',
                            textTransform: 'uppercase',
                            flexShrink: 0
                          }}>
                            {remedy.type}
                          </span>
                          <p style={{ fontSize: '1rem', lineHeight: 1.7, color: '#E5E7EB', margin: 0 }}>
                            {remedy.action}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null;

      case 'integration':
        return integration_summary ? (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Integration Summary</h2>
            <div style={{ 
              fontSize: '1.125rem', 
              lineHeight: 1.9, 
              color: '#E5E7EB',
              whiteSpace: 'pre-wrap',
              padding: '2rem',
              background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.08) 0%, rgba(16, 185, 129, 0.08) 100%)',
              borderRadius: '16px',
              border: '2px solid rgba(124, 58, 237, 0.3)'
            }}>
              {integration_summary}
            </div>
          </div>
        ) : null;

      default:
        return <div><h2>Select a section from the menu</h2></div>;
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0A0F2A', flexDirection: window.innerWidth < 768 ? 'column' : 'row' }}>
      {/* Mobile Header */}
      {window.innerWidth < 768 && (
        <div style={{ 
          background: '#151827', 
          padding: '1rem', 
          borderBottom: '2px solid #2D3748',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn btn-ghost"
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
          >
            <ArrowLeft size={18} style={{ marginRight: '0.25rem' }} />
            Back
          </button>
          <h2 style={{ 
            fontSize: '1.25rem',
            background: 'linear-gradient(90deg, #00D9FF 0%, #0891B2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
            margin: 0
          }}>
            Numerology
          </h2>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="btn btn-ghost"
            style={{ padding: '0.5rem 1rem' }}
          >
            <Menu size={24} />
          </button>
        </div>
      )}

      {/* Sidebar */}
      <div style={{ 
        width: window.innerWidth < 768 ? '100%' : '280px',
        background: '#151827', 
        borderRight: window.innerWidth < 768 ? 'none' : '2px solid #2D3748',
        padding: window.innerWidth < 768 ? '1rem' : '2rem 1rem',
        position: window.innerWidth < 768 ? 'fixed' : 'sticky',
        top: window.innerWidth < 768 ? '60px' : 0,
        height: window.innerWidth < 768 ? 'auto' : '100vh',
        overflowY: 'auto',
        zIndex: 90,
        display: window.innerWidth < 768 ? (mobileMenuOpen ? 'block' : 'none') : 'block',
        maxHeight: window.innerWidth < 768 ? 'calc(100vh - 60px)' : '100vh',
        boxShadow: window.innerWidth < 768 && mobileMenuOpen ? '0 4px 20px rgba(0,0,0,0.5)' : 'none'
      }}>
        {window.innerWidth >= 768 && (
          <>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn btn-ghost"
              style={{ width: '100%', marginBottom: '2rem', justifyContent: 'flex-start' }}
            >
              <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
              Dashboard
            </button>

            <h2 style={{ 
              fontSize: '1.5rem', 
              marginBottom: '1.5rem',
              background: 'linear-gradient(90deg, #00D9FF 0%, #0891B2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700
            }}>
              Numerology
            </h2>
          </>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {MENU_ITEMS.map(item => (
            <button
              key={item.id}
              onClick={() => {
                setActivePanel(item.id);
                if (window.innerWidth < 768) setMobileMenuOpen(false);
              }}
              className={activePanel === item.id ? 'btn btn-primary' : 'btn btn-ghost'}
              style={{ 
                justifyContent: 'flex-start', 
                padding: window.innerWidth < 768 ? '0.75rem' : '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                fontSize: window.innerWidth < 768 ? '0.9375rem' : '1rem'
              }}
            >
              <item.icon size={20} />
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: window.innerWidth < 768 ? '1rem' : '2rem', overflowY: 'auto', width: window.innerWidth < 768 ? '100%' : 'auto' }}>
        <motion.div
          key={activePanel}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {renderPanelContent()}
        </motion.div>
      </div>
    </div>
  );
}

export default Results;
